import { useEffect, useState } from 'react'
import { Mail, X } from 'lucide-react'
import { EmptyState, IconButton, Spinner } from 'components/ds'
import { fetchSessionEmailThreads, SessionEmailThread } from '../services/dispatchHelpApi'
import { EmailThreadRow } from './EmailThreadRow'
import { EmailThreadViewer } from './EmailThreadViewer'

/**
 * Right slide-over that mirrors the mobile "Emails" panel: the session's broker threads, and — on
 * select — the inbox where the operator replies as the owner. Renders as an overlay inside the
 * ChatThread pane (which is `relative`); a backdrop click closes it.
 */
export function EmailsPanel({ tabId, open, onClose }: { tabId: string; open: boolean; onClose: () => void }) {
    const [threads, setThreads] = useState<SessionEmailThread[]>([])
    const [loading, setLoading] = useState(false)
    const [selected, setSelected] = useState<SessionEmailThread | null>(null)

    useEffect(() => {
        if (!open) return
        setSelected(null)
        setLoading(true)
        fetchSessionEmailThreads(tabId)
            .then(setThreads)
            .catch(() => setThreads([]))
            .finally(() => setLoading(false))
    }, [open, tabId])

    if (!open) return null

    return (
        <div className="absolute inset-0 z-20 flex">
            {/* Backdrop — sits to the LEFT of the panel; collapses to 0 on mobile where the panel is full-width. */}
            <div className="hidden flex-1 bg-slate-900/30 backdrop-blur-[1px] transition-opacity dark:bg-black/60 sm:block" onClick={onClose} />
            <div className="flex h-full w-full flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:w-[400px]">
                {selected ? (
                    <EmailThreadViewer tabId={tabId} thread={selected} onBack={() => setSelected(null)} />
                ) : (
                    <>
                        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Emails</span>
                            <IconButton variant="ghost" size="sm" icon={<X className="h-4 w-4" />} onClick={onClose} aria-label="Close" />
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex h-full items-center justify-center">
                                    <Spinner />
                                </div>
                            ) : threads.length === 0 ? (
                                <EmptyState
                                    icon={<Mail />}
                                    title="No broker emails yet"
                                    description="This driver hasn't emailed any brokers in this session."
                                />
                            ) : (
                                threads.map((t) => <EmailThreadRow key={t.id} thread={t} onSelect={setSelected} />)
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
